/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Pluspunten and aandachtspunten DERIVED from the coded terms
 * (plan-voorwaarden-vergelijking-2026-10-07 §5.1, D2): one rule set for every
 * offer, so the same fact reads the same on every provider, plus at most one
 * sourced redactie bullet per side, always last.
 *
 * The rules are the tone table read sideways (src/lib/termsTone.ts): a fact
 * whose tone is `good` earns its pluspunt, a fact whose tone is `watch` earns
 * its WATCH aandachtspunt, `neutral` earns nothing — `other` (prose), a payout
 * after the year, a required smart charger, the price model, no minimum, a
 * 1-year looptijd, the market-norm deadline of 1 December and a yearly renewal
 * included. A MUTED fact earns an aandachtspunt of the "niet gecontroleerd /
 * niet vermeld" family: what we could not check, or what the terms do not
 * say, is a point of attention in itself — the review says so once (not
 * checked / unreadable / not all checked), a silent term says so per question
 * (`*_not_stated`), the smart charger and the KvK number each on their own
 * (D12, D23). Booking through another inboekdienstverlener (D24) is a watch
 * point — filed as `inboeker: other`, or implied when nothing is filed and the
 * record is known NOT to be on the (closed) NEa list.
 *
 * Under a review that is not `read`, the binding facts (deadline, renewal,
 * exit cost) earn nothing either way: the row and the fact table do not show
 * them then (src/lib/termsPresentation.ts), and a point about a fact nobody
 * can see would contradict the line that says it was not checked.
 *
 * ONE fact is judged outside the tone table: the offer's yearly service costs
 * (D28, Jasper 2026-10-08: "no service costs = automatically a voordeel;
 * service costs themselves are a aandachtspunt"). None (`0`) is a pluspunt,
 * any (`> 0` — the yearly figure the row carries; a monthly fee is ×12 in the
 * payload already) an aandachtspunt that names the amount, unknown (`null` /
 * absent) nothing. The "market norm stays neutral" rule holds for every other
 * fact; this one is his call, and a caller that has no offer passes nothing.
 *
 * Every offer's VERDIENMODEL is its first aandachtspunt (Jasper 2026-10-15:
 * "make verdienmodel one of the aandachtspunten and explain it for each
 * offer"): one point per model, the flat commission included — what the
 * percentage beside it is a percentage of, a staffel's ladder and the band at
 * the surface's volume, a vastrecht's bounds, a subscription's fee. It is the
 * first thing to understand about an offer, so it leads the list; it is a fact
 * of the offer record, not a catch, so it is drawn neutral
 * (DerivedBullets.bulletTone), never counts as "something to watch" (the
 * compare pages' standout, isNothingKnown) and has no row in the fact table:
 * the panel says it once. The model's own words come from
 * src/lib/offerPricingText.ts (built on offerPricingDescribe.js), the same
 * description the editors and the compare table print. A caller without an
 * offer passes no `pricing` and gets no such point.
 *
 * Items come in a FIXED order (the order of §5.1), so the same point sits in
 * the same place on every card. Pure: no React, no i18n strings — full keys
 * under `offer_terms.bullet.*` with typed variables, rendered by the client
 * and the SSR bake alike.
 */

import type {
  OfferPricing,
  PublicDossier,
  PublicEditorialBullet,
  PublicTermsReview,
} from '../types';
import { monthDayEve } from './dossier.js';
import { DEFAULT_ANNUAL_KWH } from './offerPricing.js';
import { canonicalPayout } from './payout';
import type { TermsFactTable, TermsTopic, TermsVar } from './termsPresentation';
import {
  BINDING_FIELDS,
  contractYears,
  factState,
  factTone,
  isCancelMoot,
  termsReviewStatus,
} from './termsTone';

/**
 * One derived point (a key), or the redactie's own: its text in the asked
 * language with its source, for the "bron" link the panel and the profile
 * page print after it (the compare pages leave the link out).
 */
export type VerdictItem =
  | { topic: Exclude<TermsTopic, 'editorial'>; key: string; vars?: Record<string, TermsVar> }
  | { topic: 'editorial'; text: string; sourceUrl: string; quote: string };

/** The "niet gecontroleerd / niet vermeld" family — the bullets behind a MUTED value. */
export const NOT_CHECKED_FAMILY_KEYS: readonly string[] = [
  'terms_not_checked',
  'terms_unreadable',
  'terms_incomplete',
  'cancel_not_stated',
  'after_not_stated',
  'exit_not_stated',
  'payout_not_stated',
  'smart_not_checked',
  'kvk_unknown',
].map((leaf) => `offer_terms.bullet.${leaf}`);

export interface TermsVerdict {
  pros: VerdictItem[];
  cons: VerdictItem[];
}

const NOT_CHECKED_FAMILY_SET: ReadonlySet<string> = new Set(NOT_CHECKED_FAMILY_KEYS);

/**
 * NOTHING KNOWN about the terms: nobody read them, no fact or scalar is filed
 * or implied, no redactie point exists — every answer of the fact table
 * (`termsFactTable`) says "Niet gecontroleerd" and every point of the verdict
 * is one of the "niet gecontroleerd" family. A page then says so ONCE, in the
 * review's own sentence, instead of a table of grey rows and lists of grey
 * points (the UI review's #9: the register-only profile pages). The offer's
 * verdienmodel point says nothing about the terms, so it does not count: an
 * offer whose only other points are "niet gecontroleerd" is still unchecked.
 * Pure.
 */
export function isNothingKnown(table: TermsFactTable, verdict: TermsVerdict): boolean {
  return (
    table.review.key === 'offer_terms.review.not_checked' &&
    table.rows.every(
      (row) =>
        row.question === 'termsReview' ||
        ('key' in row.value && row.value.key === 'offer_terms.value.not_checked')
    ) &&
    verdict.pros.length === 0 &&
    verdict.cons.every(
      (item) =>
        item.topic === 'pricingModel' ||
        (item.topic !== 'editorial' && NOT_CHECKED_FAMILY_SET.has(item.key))
    )
  );
}

/**
 * Whether an aandachtspunt asks a visitor to WATCH something — every one but
 * the offer's verdienmodel point, which explains the offer and judges nothing.
 * The compare pages' standout ("the one offer without an aandachtspunt")
 * counts these only.
 */
export const isWatchPoint = (item: VerdictItem): boolean => item.topic !== 'pricingModel';

export interface TermsVerdictInput {
  /** The ROW's dossier (withOfferTerms), or the provider's on the profile page. */
  dossier: PublicDossier | null | undefined;
  /** The row's looptijd ('3 jaar'); absent on the profile page (no bound_years then). */
  contractDuration?: string | null;
  payoutFrequency: string | null | undefined;
  smartChargingRequired: boolean | null | undefined;
  termsReview: PublicTermsReview | null | undefined;
  editorialPro?: PublicEditorialBullet | null;
  editorialCon?: PublicEditorialBullet | null;
  /** The annual kWh this comparison calculates with (comparer only). */
  annualKwh?: number | null;
  /** `false` = the profile carries no KvK number (D23); absent says nothing. */
  kvkKnown?: boolean;
  /** The record's NEa-list fact: `false` with no `inboeker` fact reads as "other" (D24). */
  neaListed?: boolean | null;
  /**
   * The offer's service costs a YEAR, as the row carries them (`serviceCosts`;
   * a monthly fee already ×12): `0` a pluspunt, `> 0` an aandachtspunt, `null`
   * or absent nothing (D28). The compare pages pass each offer's, the profile
   * page its first offer's, a page without an offer none.
   */
  serviceCosts?: number | null;
  /**
   * A monthly-billed offer's native monthly fee (the row's
   * `serviceCostsMonthly`): a subscription's verdienmodel point names it per
   * month, as the row's price slot does. Absent: the yearly `serviceCosts`.
   */
  serviceCostsMonthly?: number | null;
  /**
   * The offer's pricing model (`rowOfferPricing`, src/lib/offerPricing.js): its
   * verdienmodel point leads the aandachtspunten. Absent or null (no offer,
   * an unknown commission): no such point.
   */
  pricing?: OfferPricing | null;
  /**
   * The volume the surface prints the offer's percentage at — a staffel's
   * ladder emphasises the band there: the comparer's volume, a compare page's
   * (the Commissie figure's), the profile's audience default. Absent: the
   * reference household (DEFAULT_ANNUAL_KWH).
   */
  pricingKwh?: number | null;
  /** Which line of an editorial bullet to return. Default 'nl'. */
  language?: 'nl' | 'en';
}

const NS = 'offer_terms.bullet';
const bullet = (leaf: string) => `${NS}.${leaf}`;

/** Every key this module can emit — the coverage tests read it. */
export const TERMS_VERDICT_KEYS: readonly string[] = [
  // pluspunten
  'cancel_until_year_end',
  'ends_after_term',
  'exit_free',
  'paid_during_year',
  'paid_monthly_during_year',
  'paid_quarterly_during_year',
  'service_costs_none',
  'no_smart_charger',
  // aandachtspunten — the offer's verdienmodel first, one per model
  'pricing_commission',
  'pricing_subscription',
  'pricing_subscription_yearly',
  'pricing_subscription_monthly',
  'pricing_fixed_rate',
  'pricing_tiered',
  'pricing_kwh_fee',
  'pricing_capped',
  'terms_not_checked',
  'terms_unreadable',
  'terms_not_published',
  'terms_incomplete',
  'bound_years',
  'cancel_deadline_early',
  'cancel_not_stated',
  'renews_same_term',
  'after_not_stated',
  'exit_forfeit',
  'exit_fee',
  'exit_not_stated',
  'payout_not_stated',
  'service_costs',
  'smart_not_checked',
  'kvk_unknown',
  'inboeker_other',
  'inboeker_other_named',
  'minimum_kwh',
  'minimum_kwh_below',
  'minimum_points',
  'minimum_other',
].map(bullet);

const code = (entry: { value: string | string[] } | null | undefined): string | null =>
  entry && typeof entry.value === 'string' ? entry.value : null;

/**
 * The redactie's bullet as an item: its line in the asked language (the public
 * shape has the Dutch fallback baked in) and its source; null when it says
 * nothing.
 */
function editorialItem(
  b: PublicEditorialBullet | null | undefined,
  language: 'nl' | 'en'
): VerdictItem | null {
  if (!b) return null;
  const text = (language === 'en' ? b.en || b.nl : b.nl) || '';
  if (!text.trim()) return null;
  return { topic: 'editorial', text, sourceUrl: b.sourceUrl ?? '', quote: b.quote ?? '' };
}

const positive = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * The offer's verdienmodel as one point, or null without a model. The
 * figures travel raw (the renderer formats them per locale). A staffel
 * travels as its bands with the surface's volume (a `ladder` variable: rows
 * where a surface can draw them, the band at that volume emphasised; the
 * inline description where only text fits) and a vastrecht offer as its
 * model, both worded by src/lib/offerPricingText.ts, so this point and the
 * editors say the same thing. A subscription names its fee as the row's price slot does — the
 * native monthly amount when it bills per month, else the yearly one — and
 * says "vaste servicekosten" when no amount is known.
 */
function pricingItem(input: TermsVerdictInput): VerdictItem | null {
  const pricing = input.pricing;
  if (!pricing) return null;
  const topic = 'pricingModel' as const;
  switch (pricing.model) {
    case 'commission':
      return {
        topic,
        key: bullet('pricing_commission'),
        vars: { pct: { kind: 'pct', value: pricing.commissionPct } },
      };
    case 'subscription': {
      if (!positive(input.serviceCosts)) return { topic, key: bullet('pricing_subscription') };
      const monthly = positive(input.serviceCostsMonthly) ? input.serviceCostsMonthly : null;
      return {
        topic,
        key: bullet(
          monthly !== null ? 'pricing_subscription_monthly' : 'pricing_subscription_yearly'
        ),
        vars: { fee: { kind: 'eur', value: monthly ?? input.serviceCosts } },
      };
    }
    case 'fixed_rate':
      return {
        topic,
        key: bullet('pricing_fixed_rate'),
        vars: { rate: { kind: 'eur_kwh', value: pricing.eurPerKwh } },
      };
    case 'tiered': {
      const kwh = positive(input.pricingKwh) ? Math.round(input.pricingKwh) : DEFAULT_ANNUAL_KWH;
      return {
        topic,
        key: bullet('pricing_tiered'),
        vars: { model: { kind: 'ladder', tiers: pricing.tiers, atKwh: kwh } },
      };
    }
    case 'commission_plus_kwh_fee':
      return {
        topic,
        key: bullet(pricing.eurPerKwh === null ? 'pricing_capped' : 'pricing_kwh_fee'),
        vars: { model: { kind: 'offer_pricing', pricing } },
      };
    default:
      return null;
  }
}

export function termsVerdict(input: TermsVerdictInput): TermsVerdict {
  const dossier = input.dossier ?? null;
  const language = input.language ?? 'nl';
  const status = termsReviewStatus(input.termsReview);
  const read = status === 'read';
  const moot = isCancelMoot(dossier);
  const pros: VerdictItem[] = [];
  const cons: VerdictItem[] = [];

  const cancel = dossier?.cancelDeadline;
  const after = dossier?.afterTerm;
  const exit = dossier?.exitCost;
  const timing = dossier?.payoutTiming;
  const frequency = canonicalPayout(input.payoutFrequency ?? null);
  // The renewal is judged against the row's looptijd: one year renewing for
  // its own term is the yearly renewal, no catch (termsTone).
  const afterTone = factTone('afterTerm', after, { contractDuration: input.contractDuration });
  // D28: the yearly service costs, a number or nothing to judge.
  const serviceCosts =
    typeof input.serviceCosts === 'number' && Number.isFinite(input.serviceCosts)
      ? input.serviceCosts
      : null;

  // ── Pluspunten ──────────────────────────────────────────────────────────
  if (read) {
    if (!moot && factTone('cancelDeadline', cancel) === 'good') {
      pros.push({ topic: 'cancelDeadline', key: bullet('cancel_until_year_end') });
    }
    if (afterTone === 'good') {
      pros.push({ topic: 'afterTerm', key: bullet('ends_after_term') });
    }
    if (factTone('exitCost', exit) === 'good') {
      pros.push({ topic: 'exitCost', key: bullet('exit_free') });
    }
  }
  // `during_year` only when CODED so: a payout during the year that the terms
  // may claw back arrives as `other` (D11) and earns nothing.
  if (factTone('payoutTiming', timing) === 'good') {
    const leaf =
      frequency === 'Per maand'
        ? 'paid_monthly_during_year'
        : frequency === 'Per kwartaal'
          ? 'paid_quarterly_during_year'
          : 'paid_during_year';
    pros.push({ topic: 'payoutTiming', key: bullet(leaf) });
  }
  // D28: no service costs at all is in the visitor's favour (a cost: below).
  if (serviceCosts === 0) {
    pros.push({ topic: 'serviceCosts', key: bullet('service_costs_none') });
  }
  if (factTone('smartChargingRequired', input.smartChargingRequired) === 'good') {
    pros.push({ topic: 'smartChargingRequired', key: bullet('no_smart_charger') });
  }
  const pro = editorialItem(input.editorialPro, language);
  if (pro) pros.push(pro);

  // ── Aandachtspunten ─────────────────────────────────────────────────────
  // The offer's verdienmodel first: the first thing to understand about it.
  const model = pricingItem(input);
  if (model) cons.push(model);
  // What we could not check, said once.
  if (status === 'not_checked') {
    cons.push({ topic: 'termsReview', key: bullet('terms_not_checked') });
  } else if (status === 'unreadable') {
    cons.push({ topic: 'termsReview', key: bullet('terms_unreadable') });
  } else if (status === 'not_published') {
    cons.push({ topic: 'termsReview', key: bullet('terms_not_published') });
  } else {
    // Read — but did the reading answer every binding question and the payout
    // timing? "Not stated" IS an answer (its own point below); a moot deadline
    // needs none. The payout FREQUENCY is not a question of the terms (most
    // sites state it elsewhere, some nowhere): a provider whose terms were
    // read is never told "niet alle voorwaarden gecontroleerd" for a rhythm we
    // could not establish (Jasper, 2026-10-08) — the fact table prints the
    // dash, and a coded timing says what the terms do say.
    const unanswered =
      BINDING_FIELDS.some(
        (field) =>
          !(field === 'cancelDeadline' && moot) &&
          factState(field, dossier?.[field]) === 'not_checked'
      ) || factState('payoutTiming', timing) === 'not_checked';
    if (unanswered) cons.push({ topic: 'termsReview', key: bullet('terms_incomplete') });
  }

  // A looptijd beyond the norm (rows only).
  const years = contractYears(input.contractDuration);
  if (factTone('duration', years) === 'watch') {
    cons.push({
      topic: 'duration',
      key: bullet('bound_years'),
      vars: { years: { kind: 'count', value: years as number } },
    });
  }

  if (read) {
    // Opzeggen: an early deadline (watch), or none stated (muted).
    if (!moot && factTone('cancelDeadline', cancel) === 'watch') {
      const monthDay = cancel!.amount as number;
      const eve = monthDayEve(monthDay);
      cons.push({
        topic: 'cancelDeadline',
        key: bullet('cancel_deadline_early'),
        vars: {
          date: { kind: 'date', monthDay, style: 'long' },
          ...(eve !== null
            ? { date_eve: { kind: 'date', monthDay: eve, style: 'long' } as TermsVar }
            : {}),
        },
      });
    } else if (!moot && factState('cancelDeadline', cancel) === 'not_stated') {
      cons.push({ topic: 'cancelDeadline', key: bullet('cancel_not_stated') });
    }
    // Na de looptijd: a renewal for the same term (watch), or nothing stated.
    if (afterTone === 'watch') {
      cons.push({ topic: 'afterTerm', key: bullet('renews_same_term') });
    } else if (factState('afterTerm', after) === 'not_stated') {
      cons.push({ topic: 'afterTerm', key: bullet('after_not_stated') });
    }
    // Eerder stoppen: a fee or a forfeited payout (watch), or nothing stated.
    if (factTone('exitCost', exit) === 'watch') {
      cons.push(
        code(exit) === 'fee'
          ? {
              topic: 'exitCost',
              key: bullet('exit_fee'),
              vars: { amount: { kind: 'eur', value: exit!.amount as number } },
            }
          : { topic: 'exitCost', key: bullet('exit_forfeit') }
      );
    } else if (factState('exitCost', exit) === 'not_stated') {
      cons.push({ topic: 'exitCost', key: bullet('exit_not_stated') });
    }
  }

  if (factState('payoutTiming', timing) === 'not_stated') {
    cons.push({ topic: 'payoutTiming', key: bullet('payout_not_stated') });
  }
  // D28: a cost is a catch, said with its yearly amount in the page's locale.
  if (serviceCosts !== null && serviceCosts > 0) {
    cons.push({
      topic: 'serviceCosts',
      key: bullet('service_costs'),
      vars: { amount: { kind: 'eur', value: serviceCosts } },
    });
  }
  // Muted, never amber: nobody checked whether one is needed (D12).
  if (factTone('smartChargingRequired', input.smartChargingRequired) === 'unknown') {
    cons.push({ topic: 'smartChargingRequired', key: bullet('smart_not_checked') });
  }
  if (input.kvkKnown === false) {
    cons.push({ topic: 'kvk', key: bullet('kvk_unknown') });
  }
  // D24: booking through another inboekdienstverlener — filed as `other`, or
  // implied by a record that is not on the NEa list and has no fact filed.
  const inboeker = dossier?.inboeker;
  if (factTone('inboeker', inboeker, { neaListed: input.neaListed }) === 'watch') {
    const name = code(inboeker) === 'other' && inboeker?.note ? inboeker.note : '';
    cons.push(
      name
        ? {
            topic: 'inboeker',
            key: bullet('inboeker_other_named'),
            vars: { name: { kind: 'text', value: name } },
          }
        : { topic: 'inboeker', key: bullet('inboeker_other') }
    );
  }

  // A minimum the terms state is an aandachtspunt, whatever its size (Jasper,
  // 2026-10-08): a kWh minimum says so, and names the shortfall when the kWh
  // this comparison calculates with fall below it; a minimum in prose
  // (`other`) points at the fact table, where the note is the value.
  const minimum = dossier?.minimumVolume;
  if (factTone('minimumVolume', minimum, { annualKwh: input.annualKwh }) === 'watch') {
    const amount = minimum!.amount as number;
    const kwh = input.annualKwh;
    const minimumCode = code(minimum);
    cons.push(
      minimumCode === 'kwh_per_year'
        ? typeof kwh === 'number' && Number.isFinite(kwh) && kwh > 0 && kwh < amount
          ? {
              topic: 'minimumVolume',
              key: bullet('minimum_kwh_below'),
              vars: { min: { kind: 'kwh', value: amount }, kwh: { kind: 'kwh', value: kwh } },
            }
          : {
              topic: 'minimumVolume',
              key: bullet('minimum_kwh'),
              vars: { min: { kind: 'kwh', value: amount } },
            }
        : minimumCode === 'charge_points'
          ? {
              topic: 'minimumVolume',
              key: bullet('minimum_points'),
              vars: { count: { kind: 'count', value: amount } },
            }
          : { topic: 'minimumVolume', key: bullet('minimum_other') }
    );
  }

  const con = editorialItem(input.editorialCon, language);
  if (con) cons.push(con);

  return { pros, cons };
}
